import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as API from './api/userManagement-api';
import { getUsers, getUsersClients } from "./api/userManagement-api";
import { CreateUserParams, DeleteTempCallRecordingResourceParams, GetResourceFileParams, GetSingleUserParams, GetTempCallRecordingResourceParams, GetUsersParams, PaginatedRequestedUsersResponse, RequestedUser, SelectedUser, UpdateUserParams, UploadFileParams, UploadTempCallRecordingParams } from "./types";


export interface UserManagementState {
    users: any,
    status: 'idle' | 'loading' | 'failed';
    requestedUsers: PaginatedRequestedUsersResponse | undefined,
    selectedUser: SelectedUser | undefined,
    resource: any,
    requestedUser: RequestedUser | undefined,
}

const initialState: UserManagementState = {
    users: [],
    status: 'loading',
    requestedUsers: undefined,
    selectedUser: undefined,
    resource: null,
    requestedUser: undefined
};

export const getUserRequests = createAsyncThunk(
    'userManagement/getUserRequests',
    async (params: GetUsersParams) => {
        try {
            const response = await getUsersClients(params);
            return response;
        } catch (error) {
            throw error;
        }
    }
);

export const getUserNotRequests = createAsyncThunk(
    'userManagement/getUserNotRequests',
    async (params: GetUsersParams) => {
        try {
            return await getUsers(params);
        } catch (error) {
            throw error;
        }
    }
);

export const getSingleUser = createAsyncThunk(
    'userManagement/getSingleUser',
    async (params: GetSingleUserParams) => {
        try {
            return await API.getSingleClient(params);
        } catch (error) {
            throw error;
        }
    }
);

export const getSingleAccount = createAsyncThunk(
    'userManagement/getSingleAccount',
    async (params: GetSingleUserParams) => {
        try {
            return await API.getSingleAccount(params);
        } catch (error) {
            throw error;
        }
    }
);

export const saveUser = createAsyncThunk(
    'userManagement/saveUser',
    async (params: CreateUserParams) => {
        try {
            return await API.CreateUser(params);
        } catch (error) {
            throw error;
        }
    }
)

export const editUser = createAsyncThunk(
    'userManagement/editUser',
    async (params: UpdateUserParams) => {
        try {
            return await API.UpdateUser(params);
        } catch (error) {
            throw error;
        }
    }
)

export const editCurrentUser = createAsyncThunk(
    'userManagement/editCurrentUser',
    async (params: UpdateUserParams) => {
        try {
            return await API.UpdateCurrentUser(params);
        } catch (error) {
            throw error;
        }
    }
)

export const uploadFileResource = createAsyncThunk(
    'userManagement/uploadFileResource',
    async (params: UploadFileParams) => {
        try {
            return await API.UploadFile(params);
        } catch (error) {
            throw error;
        }
    }
)

export const uploadTempCallRecording = createAsyncThunk(
    'userManagement/uploadTempCallRecording',
    async (params: UploadTempCallRecordingParams) => {
        try {
            return await API.uploadTempCallRecording(params);
        } catch (error) {
            throw error;
        }
    }
)

export const getTempCallRecordingResource = createAsyncThunk(
    'userManagement/getTempCallRecordingResource',
    async (params: GetTempCallRecordingResourceParams) => {
        try {            // This is a placeholder function. You can implement the actual API call to get the temporary call recording resource here.
            return await API.getTempCallRecordingResource(params);
        } catch (error) {
            throw error;
        }
    }
)

export const deleteTempCallRecordingResource = createAsyncThunk(
    'userManagement/deleteTempCallRecordingResource',
    async (params: DeleteTempCallRecordingResourceParams) => {
        try {            // This is a placeholder function. You can implement the actual API call to get the temporary call recording resource here.
            return await API.deleteTempCallRecordingResource(params);
        } catch (error) {
            throw error;
        }
    }
)

export const uploadToQueue = createAsyncThunk(
    'userManagement/uploadToQueue',
    async (params : UploadFileParams) => {
        try{
            return await API.uploadToqueue(params);
        } catch (error) {
            throw error;
        }
    }
)

export const getFileResource = createAsyncThunk(
    'userManagement/getFileResource',
    async (params: GetResourceFileParams) => {
        try {
            return await API.getResourceFile(params);
        } catch (error) {
            throw error;
        }
    }
)

const UserManagementSlice = createSlice({
    name: 'userManagement',
    initialState,
    reducers: {
        setSelectedUser: (state, action: PayloadAction<any>) => {
            state.selectedUser = action.payload;
        },
        clearFileResource: (state) => {
            state.resource = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getUserRequests.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(getUserRequests.fulfilled, (state, { payload }) => {
                state.status = 'idle';
                state.requestedUsers = payload
            })
            .addCase(getUserRequests.rejected, (state) => {
                state.status = 'failed';
            })
            .addCase(getUserNotRequests.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(getUserNotRequests.fulfilled, (state, { payload }) => {
                state.status = 'idle';
                state.users = payload
            })
            .addCase(getUserNotRequests.rejected, (state) => {
                state.status = 'failed';
            })
            .addCase(saveUser.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(saveUser.fulfilled, (state, { payload }) => {
                state.status = 'idle';
            })
            .addCase(saveUser.rejected, (state) => {
                state.status = 'failed';
            })
            .addCase(editUser.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(editUser.fulfilled, (state, { payload }) => {
                state.status = 'idle';
            })
            .addCase(editUser.rejected, (state) => {
                state.status = 'failed';
            })

            .addCase(uploadFileResource.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(uploadFileResource.fulfilled, (state, { payload }) => {
                state.status = 'idle';
            })
            .addCase(uploadFileResource.rejected, (state) => {
                state.status = 'failed';
            })
            .addCase(getFileResource.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(getFileResource.fulfilled, (state, { payload }) => {
                state.status = 'idle';
                state.resource = payload?.resource
            })
            .addCase(getFileResource.rejected, (state) => {
                state.status = 'failed';
            })
            .addCase(getSingleUser.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(getSingleUser.fulfilled, (state, { payload }) => {
                state.status = 'idle';
                state.selectedUser = payload;
            })
            .addCase(getSingleUser.rejected, (state) => {
                state.status = 'failed';
            })
            .addCase(getSingleAccount.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(getSingleAccount.fulfilled, (state, { payload }) => {
                state.status = 'idle';
                state.selectedUser = payload.user;
            })
            .addCase(getSingleAccount.rejected, (state) => {
                state.status = 'failed';
            })
    },

})
// Export the action creator
export const { setSelectedUser, clearFileResource } = UserManagementSlice.actions;

export default UserManagementSlice.reducer;
